import { useRequest } from 'features/request/useRequest';
import type { WorkOrderChangePriceItemFilterInput } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getWhereFromFilter, getWhereVariables } from '../helpers';
import { uerWorkOrderChangePriceItemsGql } from '../workOrderChangeQueries';

type TWorkOrderChangeData = {
    workOrderChangeId: number;
    objectFilterData: {
        rootObjectUniqueId?: string | null;
        objectUniqueId?: string | null;
        workOrderRootObjectId: number | null | undefined;
        workOrderChangeRootObjectId: number | null | undefined;
        workOrderObjectId?: number | null;
        workOrderChangeObjectId?: number | null;
    } | null;
    constructionTypeId: number;
    tableKey: string;
};
export const useWorkOrderChangeData = ({
    workOrderChangeId,
    constructionTypeId,
    objectFilterData,
    tableKey,
}: TWorkOrderChangeData) => {
    const [filtersWhere, setFiltersWhere] = useState<WorkOrderChangePriceItemFilterInput | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prev) => ({
            ...prev,
            ...getWhereFromFilter(fieldName, checkedValues),
        }));
    }, []);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    tableKey,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                ),
            }),
            [changeFilterHandler, tableKey],
        ),
    );

    const [data, , , request] = useRequest({
        query: uerWorkOrderChangePriceItemsGql,
        withEffect: false,
        requestId: 'workOrderChangeData',
    });

    const workOrderChangeDataVariables = useMemo(() => {
        if (!workOrderChangeId && !constructionTypeId) {
            return undefined;
        }

        return {
            constructionTypeId,
            workOrderChangeId,
            ...getWhereVariables(objectFilterData, filtersWhere),
        };
    }, [constructionTypeId, filtersWhere, objectFilterData, workOrderChangeId]);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderChangeData',
        requestFn: request,
        requestVariables: workOrderChangeDataVariables,
    });

    useEffect(() => {
        if (data?.uerWorkOrderChangePriceItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                data.uerWorkOrderChangePriceItems.items,
                data.uerWorkOrderChangePriceItems?.totalCount,
            );
        }
    }, [data?.uerWorkOrderChangePriceItems?.items, data?.uerWorkOrderChangePriceItems?.totalCount, getRowParams]);

    const totalItems = useMemo(
        () => data?.uerWorkOrderChangePriceItems?.totalCount,
        [data?.uerWorkOrderChangePriceItems?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    return {
        workOrderChangePriceData: dataSource,
        fetchedItems,
        totalItems,
        changeFilterHandler,
    };
};
